import { useAppDispatch, useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import { AlertModalProps } from 'components/common/AlertModal';
import { ConfirmModalProps } from 'components/common/ConfirmModal';
import { GlobalModalContext } from 'components/common/GlobalModal';
import { StoragePlace } from 'features/AdminMaster/models/storagePlace';
import storagePlaceService from 'features/AdminMaster/storagePlaceService';
import { selectStoragePlaceList, storagePlaceActions } from 'features/AdminMaster/storagePlaceSlice';
import { CommonResponse } from 'models';
import React, { FormEvent, useEffect, useState } from 'react';
import { Button, Form, InputGroup, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { MODAL_TYPES } from 'utils';
import { handleServiceError } from 'utils/common';

function StoragePlaceFeature() {
  const dispatch = useAppDispatch();
  const [validated, setValidated] = useState(false);
  const { t } = useTranslation();
  const { showGlobalModal } = GlobalModalContext.useGlobalModalContext();

  //state
  const storagePlaceList = useAppSelector(selectStoragePlaceList);

  function displayError(error: AxiosError) {
    if (error) {
      const errorData = error.response?.data as CommonResponse<any>;
      if (!errorData.data) {
        showGlobalModal<AlertModalProps>(MODAL_TYPES.ALERT_MODAL, {
          message: errorData.message,
          title: t('App.error'),
        });
      } else {
        let errorMessage = '';
        for (const property in errorData.data) {
          errorMessage += errorData.data[property].join('<br />');
        }

        if (errorMessage.length > 0) {
          showGlobalModal<AlertModalProps>(MODAL_TYPES.ALERT_MODAL, {
            message: errorMessage,
            title: t('App.error'),
          });
        }
      }
    }
  }

  useEffect(() => {
    dispatch(storagePlaceActions.fetchStoragePlaceList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    event.stopPropagation();

    const form = event.currentTarget as HTMLFormElement;
    if (!form.checkValidity()) {
      setValidated(true);
    } else {
      const name = form['storage_place_name'].value.trim();

      (async () => {
        try {
          await storagePlaceService.add(name);
          dispatch(storagePlaceActions.fetchStoragePlaceList());
          form.reset();
          setValidated(false);
          showGlobalModal<AlertModalProps>(MODAL_TYPES.ALERT_MODAL, {
            message: t('AdminMasterFeature.save_data_successful'),
            title: t('App.info'),
          });
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(e as AxiosError);
            setValidated(true);
          }
        }
      })();
    }
  }

  function handleInputNameBlur() {
    setValidated(false);
  }

  function handleChangeName(e: React.ChangeEvent<HTMLInputElement>) {
    const errorHelperElem = e.currentTarget.parentElement?.querySelector('.error-helper');
    if (errorHelperElem) errorHelperElem.innerHTML = '';
  }

  function handleUpdate(rowIndex: number) {
    const inputNameElem = document.querySelector('#storage-place-name-' + rowIndex) as HTMLInputElement;
    if (inputNameElem) {
      const name = inputNameElem.value.trim();
      const data: Partial<StoragePlace> = {
        id: storagePlaceList[rowIndex].id,
        name: name,
      };

      (async () => {
        try {
          await storagePlaceService.update([data]);
          dispatch(storagePlaceActions.fetchStoragePlaceList());
          showGlobalModal<AlertModalProps>(MODAL_TYPES.ALERT_MODAL, {
            message: t('AdminMasterFeature.save_data_successful'),
            title: t('App.info'),
          });
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            const errorResponse = (e as AxiosError).response?.data as CommonResponse<any>;
            const errorData = errorResponse.data.error_data;
            const rowError = document.querySelector('#storage-place-row-' + errorData.id);
            if (rowError) {
              rowError.scrollIntoView({
                behavior: 'auto',
                block: 'center',
                inline: 'center',
              });
              for (const errorKey in errorResponse.data.errors) {
                const errorControlTextElem = rowError.querySelector('.error-' + errorKey);
                if (errorControlTextElem) {
                  errorControlTextElem.innerHTML = errorResponse.data.errors[errorKey];
                }
              }
            }
          }
        }
      })();
    }
  }

  function handleDelete(rowIndex: number) {
    if (storagePlaceList[rowIndex]) {
      showGlobalModal<ConfirmModalProps>(MODAL_TYPES.CONFIRM_MODAL, {
        title: t('App.warning'),
        message: t('AdminMasterFeature.confirm_delete'),
        onConfirm: function () {
          (async () => {
            try {
              await storagePlaceService.delete(storagePlaceList[rowIndex].id);
              dispatch(storagePlaceActions.fetchStoragePlaceList());
              showGlobalModal<AlertModalProps>(MODAL_TYPES.ALERT_MODAL, {
                message: t('AdminMasterFeature.save_data_successful'),
                title: t('App.info'),
              });
            } catch (e) {
              if (!handleServiceError(showGlobalModal, e as AxiosError)) {
                displayError(e as AxiosError);
              }
            }
          })();
        },
      });
    }
  }

  function handleRestore(rowIndex: number) {
    if (storagePlaceList[rowIndex]) {
      showGlobalModal<ConfirmModalProps>(MODAL_TYPES.CONFIRM_MODAL, {
        title: t('App.warning'),
        message: t('AdminMasterFeature.confirm_restore'),
        onConfirm: function () {
          (async () => {
            try {
              await storagePlaceService.restore(storagePlaceList[rowIndex].id);
              dispatch(storagePlaceActions.fetchStoragePlaceList());
              showGlobalModal<AlertModalProps>(MODAL_TYPES.ALERT_MODAL, {
                message: t('AdminMasterFeature.save_data_successful'),
                title: t('App.info'),
              });
            } catch (e) {
              if (!handleServiceError(showGlobalModal, e as AxiosError)) {
                displayError(e as AxiosError);
              }
            }
          })();
        },
      });
    }
  }

  return (
    <>
      <Table bordered hover className="mb-0">
        <thead className="bg-violet">
          <tr>
            <th colSpan={2}>{t('AdminMasterFeature.asset_storage_place_headtitle')}</th>
          </tr>
          <tr>
            <th className="asset-status-col1">{t('AdminMasterFeature.name')}</th>
            <th className="asset-status-col2">{t('AdminMasterFeature.action')}</th>
          </tr>
        </thead>
      </Table>
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Table bordered className="mb-0" style={{ borderTop: '1px solid #fff' }}>
          <tbody>
            <tr>
              <td className="asset-status-col1">
                <InputGroup className="flex-grow-1 flex-shrink-1 me-4" size="sm" hasValidation>
                  <Form.Control
                    id="storage-place-name"
                    name="storage_place_name"
                    type="text"
                    required
                    onBlur={handleInputNameBlur}
                  />
                  <Form.Control.Feedback type="invalid">
                    {t('AdminMasterFeature.warning_required_field')}
                  </Form.Control.Feedback>
                </InputGroup>
              </td>
              <td className="asset-status-col2">
                <Button type="submit" variant="primary" size="sm" className="btn-action">
                  {t('AdminMasterFeature.sign_up')}
                </Button>
              </td>
            </tr>
          </tbody>
        </Table>
      </Form>
      <div className="fit-content flex-grow-1 flex-shrink-1">
        <Table bordered className="mb-0" style={{ borderTop: '1px solid #fff' }}>
          <tbody>
            {storagePlaceList.map(function (item, index) {
              return (
                <tr
                  id={`storage-place-row-${item.id}`}
                  key={item.id}
                  className={item.stopped_flg ? 'table-secondary' : ''}
                >
                  <td className="asset-status-col1">
                    <Form.Control
                      id={`storage-place-name-${index}`}
                      defaultValue={item.name}
                      data-index={index}
                      size="sm"
                      onChange={handleChangeName}
                    ></Form.Control>
                    <span className="error-helper error-name small text-danger"></span>
                  </td>
                  <td className="asset-status-col2">
                    <Button className="btn--green btn-action me-1" size="sm" onClick={handleUpdate.bind(null, index)}>
                      {t('AdminMasterFeature.change')}
                    </Button>
                    {!item.stopped_flg && (
                      <Button
                        className="btn--red btn-action"
                        size="sm"
                        disabled={!item.can_delete}
                        onClick={handleDelete.bind(null, index)}
                      >
                        {t('AdminMasterFeature.stop')}
                      </Button>
                    )}
                    {item.stopped_flg && (
                      <Button className="btn--yellow btn-action" size="sm" onClick={handleRestore.bind(null, index)}>
                        {t('AdminMasterFeature.valid')}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </>
  );
}

export default StoragePlaceFeature;
